import PageLayout from '@/components/feature/PageLayout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo } from '@/mocks/site';

export default function AboutPage() {
  const jsonLdOrg = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.contactInfo.email,
    foundingDate: siteInfo.founded,
  };

  const jsonLdFaq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: siteInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <PageLayout>
      <SEO
        title={`关于我们 - ${siteInfo.name} | 品牌介绍与联系方式`}
        description={`${siteInfo.description} 了解黑料聚合网的品牌故事、发展历程、用户口碑与联系方式。创立于${siteInfo.founded}年，服务${siteInfo.stats.users}用户。`}
        keywords="黑料聚合网,关于我们,品牌介绍,联系方式,黑料,51黑料网"
        canonicalUrl="https://51heiliao.com/about"
        jsonLd={[jsonLdOrg, jsonLdFaq]}
      />
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: '关于我们' },
        ]}
      />

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于 {siteInfo.name}
            </h1>
            <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {siteInfo.tagline}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16">
            {Object.entries(siteInfo.stats).map(([key, value]) => (
              <div key={key} className="bg-background-100 rounded-xl p-5 text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                  {value}
                </div>
                <div className="text-xs text-foreground-500">
                  {key === 'users' && '注册用户'}
                  {key === 'dailyVisits' && '日访问量'}
                  {key === 'articles' && '内容总数'}
                  {key === 'platforms' && '聚合平台'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
            品牌介绍
          </h2>
          <div className="space-y-6">
            <div className="bg-background-50 rounded-xl p-6 md:p-8">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">我们的故事</h3>
              <p className="text-foreground-700 leading-relaxed">{siteInfo.brandIntro}</p>
            </div>
            <div className="bg-background-50 rounded-xl p-6 md:p-8">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">用户口碑</h3>
              <p className="text-foreground-700 leading-relaxed">{siteInfo.reputation}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 md:px-6 py-14">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
          发展历程
        </h2>
        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px" />
          <div className="space-y-8">
            {siteInfo.timeline.map((item, index) => (
              <div
                key={item.year}
                className={`relative flex items-start gap-6 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0`}>
                  <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5 inline-block">
                    <span className="text-xs font-bold text-primary-500 bg-primary-50 px-2 py-0.5 rounded-full">
                      {item.year}
                    </span>
                    <p className="text-sm text-foreground-700 mt-2 leading-relaxed">{item.event}</p>
                  </div>
                </div>
                <div className="hidden md:block flex-1" />
                <div className="absolute left-2 md:left-1/2 top-1 w-5 h-5 rounded-full bg-primary-500 border-4 border-background-50 -translate-x-1/2 z-10" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
            常见问题
          </h2>
          <div className="space-y-3">
            {siteInfo.faqs.map((faq, index) => (
              <details
                key={index}
                className="bg-background-50 rounded-xl border border-background-200/70 group"
              >
                <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                  <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                    {faq.question}
                  </span>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </summary>
                <div className="px-5 pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 md:px-6 py-14">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
          联系我们
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
            <span className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-mail-line text-xl text-accent-700"></i>
            </span>
            <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
            <p className="text-sm text-foreground-600">{siteInfo.contactInfo.email}</p>
          </div>
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
            <span className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-wechat-line text-xl text-accent-700"></i>
            </span>
            <h4 className="text-sm font-semibold text-foreground-900 mb-1">官方微信</h4>
            <p className="text-sm text-foreground-600">{siteInfo.contactInfo.wechat}</p>
          </div>
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
            <span className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-briefcase-line text-xl text-accent-700"></i>
            </span>
            <h4 className="text-sm font-semibold text-foreground-900 mb-1">商务合作</h4>
            <p className="text-sm text-foreground-600">{siteInfo.contactInfo.cooperation}</p>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}